import { toast } from "@assetwatch/ui";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import { ACTIVE_PAGE_REFRESH_INTERVAL_MS } from "../lib/activePageRefresh";
import { assigneeValue, parseAssigneeValue } from "../lib/assigneeTokens";
import type { WorkOrderId } from "../lib/workIds";

const assignmentSchema = z.object({
  id: z.number(),
  kind: z.enum(["user", "team"]),
  userId: z.number().nullable(),
  teamId: z.number().nullable(),
  name: z.string(),
  initials: z.string(),
  assignedOn: z.string(),
});
const responseSchema = z.object({ assignments: z.array(assignmentSchema) });
type Target = { userId?: number; teamId?: number; retainTeam?: boolean };

async function errorMessage(response: Response, fallback: string) {
  const body = (await response.json().catch(() => null)) as {
    error?: string;
  } | null;
  return body?.error ?? fallback;
}

function mutationError(error: unknown, fallback: string) {
  toast.error(error instanceof Error ? error.message : fallback, {
    icon: true,
  });
}

export function useWorkOrderAssignmentsQuery(workOrderId: WorkOrderId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  return useQuery(
    workOrderAssignmentsQueryOptions({
      customerId: selectedCustomer?.id,
      facilityId: selectedFacility?.id,
      workOrderId,
    }),
  );
}

/**
 * Builds the typed active-assignment query contract for a Work Order.
 * Customer and facility scope are required for execution; the options own the
 * `work-order-assignments` cache key used by assignment detail consumers.
 */
export function workOrderAssignmentsQueryOptions({
  customerId,
  facilityId,
  workOrderId,
}: {
  customerId?: string;
  facilityId?: string;
  workOrderId: WorkOrderId;
}) {
  return queryOptions({
    queryKey: ["work-order-assignments", customerId, facilityId, workOrderId],
    queryFn: async () => {
      if (!customerId || !facilityId)
        throw new Error("A customer and facility must be selected.");
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["work-orders"][":workOrderId"].assignments.$get({
        param: {
          customerId,
          facilityId,
          workOrderId,
        },
      });
      if (!response.ok)
        throw new Error(
          await errorMessage(response, "Unable to load assignments."),
        );
      return responseSchema.parse(await response.json());
    },
    enabled: Boolean(customerId && facilityId && workOrderId),
    refetchInterval: ACTIVE_PAGE_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    meta: { errorMessage: "Unable to load Work Order assignments." },
  });
}

export function useWorkOrderAssignmentMutations(workOrderId: WorkOrderId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  const invalidate = async () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["work-order-assignments"] }),
      queryClient.invalidateQueries({ queryKey: ["work-order-detail"] }),
      queryClient.invalidateQueries({ queryKey: ["work-orders"] }),
    ]);
  const mutation = useMutation({
    mutationFn: async ({ target }: { target: Target }) => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["work-orders"][":workOrderId"].assignments.$post({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          workOrderId,
        },
        json: target,
      });
      if (!response.ok)
        throw new Error(
          await errorMessage(response, "Unable to assign Work Order."),
        );
      return response.json();
    },
    onSuccess: async () => {
      toast.add("Work Order assignment updated.", { icon: true });
      await invalidate();
    },
    onError: (error) =>
      mutationError(error, "Unable to update Work Order assignment."),
  });
  const remove = useMutation({
    mutationFn: async (assignmentId: number) => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["work-orders"][":workOrderId"].assignments[":assignmentId"].$delete({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          workOrderId,
          assignmentId: String(assignmentId),
        },
      });
      if (!response.ok)
        throw new Error(
          await errorMessage(response, "Unable to remove assignment."),
        );
      return response.json();
    },
    onSuccess: async () => {
      toast.add("Work Order assignment removed.", { icon: true });
      await invalidate();
    },
    onError: (error) => mutationError(error, "Unable to remove assignment."),
  });
  /**
   * Applies the difference between the current assignees and the next set of
   * `user:<id>` / `team:<id>` tokens: additions first, then removals, one at a
   * time. Team assignments are always retained when a user is added. It stops
   * at the first failure and surfaces that failure's message, so a partial
   * change is never reported as a success; the caches are refreshed once at
   * the end either way.
   */
  const replace = useMutation({
    mutationFn: async ({
      current,
      next,
    }: {
      current: WorkOrderAssignment[];
      next: string[];
    }) => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      const param = {
        customerId: selectedCustomer.id,
        facilityId: selectedFacility.id,
        workOrderId,
      };
      const currentTokens = current.map(assigneeValue);
      const toAdd = next.filter((token) => !currentTokens.includes(token));
      const toRemove = current.filter(
        (assignment) => !next.includes(assigneeValue(assignment)),
      );
      for (const token of toAdd) {
        const response = await workClient.v1.customers[
          ":customerId"
        ].facilities[":facilityId"]["work-orders"][
          ":workOrderId"
        ].assignments.$post({
          param,
          json: { ...parseAssigneeValue(token), retainTeam: true },
        });
        if (!response.ok)
          throw new Error(
            await errorMessage(response, "Unable to assign Work Order."),
          );
      }
      for (const assignment of toRemove) {
        const response = await workClient.v1.customers[
          ":customerId"
        ].facilities[":facilityId"]["work-orders"][":workOrderId"].assignments[
          ":assignmentId"
        ].$delete({
          param: { ...param, assignmentId: String(assignment.id) },
        });
        if (!response.ok)
          throw new Error(
            await errorMessage(response, "Unable to remove assignment."),
          );
      }
    },
    onSuccess: () => {
      toast.add("Work Order assignment updated.", { icon: true });
    },
    onError: (error) =>
      mutationError(error, "Unable to update Work Order assignment."),
    onSettled: invalidate,
  });
  return { assign: mutation, remove, replace };
}

export type WorkOrderAssignment = z.infer<typeof assignmentSchema>;
